import { NavLink, useLocation } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { ManaWordmark, ManaMark } from "@/components/mh/Brand";
import { useApprovals } from "@/components/mh/hooks";
import { houseNav, opsNav, adminNav, isActivePath } from "@/components/layout/nav";
import { useAuth } from "@/context/AuthContext";

const Item = ({ item, open, active, count }) => {
  const Icon = item.icon;
  return (
    <NavLink to={item.path} data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
      className={cn("relative flex items-center gap-3 px-3 h-10 rounded-lg text-sm transition-[background-color,color] duration-200",
        active ? "bg-white/[0.07] text-foreground" : "text-muted-foreground hover:text-foreground hover:bg-white/[0.04]")}>
      {active && <span className="absolute left-0 top-2 bottom-2 w-[2px] rounded-full bg-[#D4FF3A]" />}
      <Icon className="w-[18px] h-[18px] flex-shrink-0" strokeWidth={1.5} />
      {open && <span className="truncate">{item.label}</span>}
      {item.badge && count > 0 && (
        <span className={cn("font-mono text-[10px] rounded-full bg-[#FFB020] text-black px-1.5 min-w-[18px] text-center", open ? "ml-auto" : "absolute top-1 right-1")} data-testid="nav-approvals-badge">{count}</span>
      )}
    </NavLink>
  );
};

export default function Sidebar({ open, onToggle }) {
  const { pathname } = useLocation();
  const { items } = useApprovals();
  const { current } = useAuth();
  const logo = current?.logo_url;
  const wsName = current?.name || "";
  return (
    <aside className={cn("hidden lg:flex flex-col border-r border-white/[0.07] bg-[#050508] transition-[width] duration-300 flex-shrink-0", open ? "w-64" : "w-[72px]")} data-testid="sidebar">
      <div className="h-16 flex items-center px-4 border-b border-white/[0.07] gap-3" data-testid="sidebar-brand">
        {logo
          ? <img src={logo} alt={wsName} className="w-8 h-8 rounded-md object-contain bg-black/30 border border-white/10" data-testid="sidebar-ws-logo" />
          : (open ? <ManaWordmark /> : <ManaMark />)}
        {open && logo && <span className="font-display text-base truncate" title={wsName} data-testid="sidebar-ws-name">{wsName}</span>}
      </div>
      <nav className="flex-1 py-4 px-2 overflow-y-auto">
        {open && <div className="px-3 pb-2 font-mono text-[9px] uppercase tracking-[0.28em] text-muted-foreground/70">The house</div>}
        <div className="space-y-0.5">{houseNav.map((i) => <Item key={i.path} item={i} open={open} active={isActivePath(pathname, i.path)} count={items.length} />)}</div>
        {open && <div className="px-3 pt-6 pb-2 font-mono text-[9px] uppercase tracking-[0.28em] text-muted-foreground/70">Administration</div>}
        {!open && <div className="my-4 mx-3 border-t border-white/[0.07]" />}
        <div className="space-y-0.5">{adminNav.map((i) => <Item key={i.path} item={i} open={open} active={isActivePath(pathname, i.path)} />)}</div>
      </nav>
      <button onClick={onToggle} className="flex items-center justify-center py-3 border-t border-white/[0.07] text-muted-foreground hover:text-foreground transition-colors" data-testid="sidebar-toggle">
        {open ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
      </button>
      <div className="p-4 border-t border-white/[0.07] flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-[#D4FF3A] mh-pulse" />
        {open && <span className="font-mono text-[10px] text-muted-foreground tracking-wider">HOUSE ONLINE</span>}
      </div>
    </aside>
  );
}
